import { Navigate, Route, Routes } from 'react-router'
import type { JSX } from 'react'
import { RequireAnonymous, RequireAuth } from '@/auth/guards'
import { RequireTenant } from '@/tenant/RequireTenant'
import { AppShell } from '@/shell/AppShell'
import { AcceptInvitation } from '@/pages/public/AcceptInvitation'
import { CheckEmail } from '@/pages/public/CheckEmail'
import { ForgotPassword } from '@/pages/public/ForgotPassword'
import { Login } from '@/pages/public/Login'
import { OutreachUnsubscribe } from '@/pages/public/OutreachUnsubscribe'
import { ResetPassword } from '@/pages/public/ResetPassword'
import { SupportAccess } from '@/pages/public/SupportAccess'
import { Signup } from '@/pages/public/Signup'
import { Unsubscribe } from '@/pages/public/Unsubscribe'
import { VerifyEmail } from '@/pages/public/VerifyEmail'
import { VerifyEmailChange } from '@/pages/public/VerifyEmailChange'
import { VerifyMobile } from '@/pages/public/VerifyMobile'
import { BrandVoiceStep } from '@/onboarding/BrandVoiceStep'
import { Finished } from '@/onboarding/Finished'
import { KeywordsStep } from '@/onboarding/KeywordsStep'
import { LinkedInStep } from '@/onboarding/LinkedInStep'
import { OnboardingBoundary } from '@/onboarding/OnboardingBoundary'
import { OnboardingIndex } from '@/onboarding/OnboardingIndex'
import { Resume } from '@/onboarding/Resume'
import { SearchConsoleStep } from '@/onboarding/SearchConsoleStep'
import { SenderStep } from '@/onboarding/SenderStep'
import { SiteStep } from '@/onboarding/SiteStep'
import { WizardLayout } from '@/onboarding/WizardLayout'
import { BillingPage } from '@/pages/billing/BillingPage'
import { CheckoutReturn } from '@/pages/billing/CheckoutReturn'
import { PlansPage } from '@/pages/billing/PlansPage'
import { UsagePage } from '@/pages/usage/UsagePage'
import { SeoDashboard } from '@/pages/seo/Dashboard'
import { SearchPerformance } from '@/pages/seo/SearchPerformance'
import { TechnicalAudit } from '@/pages/seo/TechnicalAudit'
import { Keywords } from '@/pages/seo/Keywords'
import { Cannibalization } from '@/pages/seo/Cannibalization'
import { ApprovalQueue } from '@/pages/seo/ApprovalQueue'
import { ContentPipeline } from '@/pages/seo/ContentPipeline'
import { RunTriggers } from '@/pages/seo/RunTriggers'
import { PublishTargets } from '@/pages/seo/PublishTargets'
import { Sites } from '@/pages/seo/Sites'
import { ReasoningTrail } from '@/pages/seo/ReasoningTrail'
import { Compose } from '@/pages/outreach/Compose'
import { DashboardPage } from '@/pages/outreach/DashboardPage'
import { InboxPage } from '@/pages/outreach/InboxPage'
import { LeadDetail } from '@/pages/outreach/LeadDetail'
import { NichePacks } from '@/pages/outreach/NichePacks'
import { Leads } from '@/pages/outreach/Leads'
import { Pipeline } from '@/pages/outreach/Pipeline'
import { PublishPost } from '@/pages/outreach/PublishPost'
import { Suppression } from '@/pages/outreach/Suppression'
import { NotFound } from '@/pages/NotFound'
import { Organisations } from '@/pages/Organisations'
import { OrganisationPanel } from '@/pages/settings/OrganisationPanel'
import { NotificationsPanel } from '@/pages/settings/NotificationsPanel'
import { SessionsPanel } from '@/pages/settings/SessionsPanel'
import { SettingsLayout } from '@/pages/settings/SettingsLayout'
import { BudgetPanel } from '@/pages/settings/BudgetPanel'
import { BrandVoicePanel } from '@/pages/settings/BrandVoicePanel'
import { ConnectionsPanel } from '@/pages/settings/ConnectionsPanel'
import { DangerZonePanel } from '@/pages/settings/DangerZonePanel'
import { TeamPanel } from '@/pages/settings/TeamPanel'

/**
 * The route table.
 *
 * Five bands, and which band a route is in is a security statement rather than
 * a layout one:
 *
 *  1. **Token pages** — no guard at all. Each is opened from a link in an email
 *     by someone who may not be signed in, on a device that may not be theirs.
 *     `/accept-invitation` is here even though accepting needs a session,
 *     because the page has to *say* that rather than bounce and lose the token.
 *  2. **Anonymous-only** — sign in and sign up, which redirect a signed-in user
 *     onward rather than showing them a form they have no use for.
 *  3. **Signed in, no organisation needed** — the organisation picker, and the
 *     two confirmation screens a brand-new account lands on. `/verify-mobile`
 *     and `/check-email` are here rather than in band 2 because signup leaves a
 *     session: they are reached *while* signed in, and a `RequireAnonymous`
 *     guard would redirect straight past them.
 *  4. **The onboarding wizard** — needs an organisation, but not the shell. A
 *     tenant part-way through setup has nothing for a sidebar to describe.
 *  5. **Inside the shell** — everything that needs an organisation selected on
 *     the session.
 *
 * Declarative `<Routes>` rather than a data router: nothing here loads through
 * a route loader, and `MemoryRouter` makes the whole table testable without a
 * browser history.
 */
export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      {/* 1 — links already in the wild */}
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/verify-email-change" element={<VerifyEmailChange />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      <Route path="/check-email" element={<CheckEmail />} />
      <Route path="/verify-mobile" element={<VerifyMobile />} />
      <Route path="/notifications/unsubscribe" element={<Unsubscribe />} />
      {/* S-B11. Band 1, and it belongs here rather than behind a guard for the
          same reason every other route in this band does: whoever opens it is
          not yet anybody on this host. What authorises it is the single-use
          token in the fragment, which it exchanges for a borrowed session. */}
      <Route path="/support-access" element={<SupportAccess />} />
      {/* S-B12. The §8 one-click link, and the only public page whose reader
          has no relationship with AugRank — they were contacted by a tenant. */}
      <Route path="/outreach/unsubscribe" element={<OutreachUnsubscribe />} />

      {/* 2 */}
      <Route element={<RequireAnonymous />}>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
      </Route>

      {/* 3, 4 and 5 */}
      <Route element={<RequireAuth />}>
        <Route path="/organisations" element={<Organisations />} />

        <Route element={<RequireTenant />}>
          <Route element={<OnboardingBoundary />}>
            <Route path="/onboarding" element={<WizardLayout />}>
              <Route index element={<OnboardingIndex />} />
              <Route path="site" element={<SiteStep />} />
              <Route path="search-console" element={<SearchConsoleStep />} />
              <Route path="linkedin" element={<LinkedInStep />} />
              <Route path="sender" element={<SenderStep />} />
              <Route path="keywords" element={<KeywordsStep />} />
              <Route path="brand-voice" element={<BrandVoiceStep />} />
              <Route path="done" element={<Finished />} />
              <Route path="resume" element={<Resume />} />
            </Route>

            <Route element={<AppShell />}>
              <Route index element={<Navigate to="/seo" replace />} />
              {/* S-B32a. `/seo` is the dashboard rather than `ModuleOverview`'s
                  placeholder — S-D5 screen 1, the screen a tenant opens first.
                  Ranking leads because ranking is the promise; the Core Web
                  Vitals and burn panels (S-B32b) and the reasoning trail
                  (S-B32c) land below it. */}
              <Route path="/seo" element={<SeoDashboard />} />
              {/* S-B22a. The SEO module's first real screen: the Search Console
                  property picker and the figures it produces. */}
              <Route path="/seo/search-performance" element={<SearchPerformance />} />
              {/* S-G7. `seo#sites` — audit row §A3, and the last of
                  `navigation.ts`'s disabled SEO entries. Below *Search
                  performance* because that is where S-D3 put it, and one route
                  rather than a list and a separate uptime screen: the prototype
                  draws both on one page and the uptime table is the site list
                  answering a second question about the same rows. */}
              <Route path="/seo/sites" element={<Sites />} />
              {/* S-B23. The technical audit, organised by who acts rather than
                  by severity — S-D5's own note argues the shape. */}
              <Route path="/seo/audit" element={<TechnicalAudit />} />
              {/* S-B24. Rank tracking and the competitor gap. Cannibalization is
                  a sub-path rather than a fourth tab or a sibling: a cross-site
                  conflict belongs to two sites at once, so it cannot live under
                  the per-site tab strip — and nesting it under `/seo/keywords`
                  is what gives it the *Keywords* breadcrumb without adding a
                  navigation row S-D3 never approved. */}
              <Route path="/seo/keywords" element={<Keywords />} />
              <Route path="/seo/keywords/cannibalization" element={<Cannibalization />} />
              {/* S-G6. `seo#pipeline`. The sidebar's *Content* section, which
                  S-D3 approved and which has rendered disabled since S-B25
                  closed — audit row §A2. One route rather than a board and a
                  separate gate screen, because the mockup's detail card is the
                  board's selection read out: the gate's verdict is only ever
                  read about a document somebody just pointed at. */}
              <Route path="/seo/content" element={<ContentPipeline />} />
              {/* S-B27. The approval queue and the per-site gating are one
                  route, because they are one decision at two moments: the queue
                  is *"may this go out"* answered item by item, and the gating is
                  the same question answered in advance. Putting the switch that
                  turns approvals off into `/settings` would hide it from the
                  person doing the approving, which is who changes it. */}
              <Route path="/seo/approvals" element={<ApprovalQueue />} />
              {/* S-B29. `seo#triggers`. Its own route rather than a panel on
                  `/settings`, because the schedule is per site and the settings
                  screen is per organisation — an agency running a staging domain
                  and a client's live domain wants different answers, which is the
                  same axis `run_schedules.site_id` is unique on. */}
              <Route path="/seo/triggers" element={<RunTriggers />} />
              {/* S-G5. `seo#targets`. Its own route for the same reason
                  `/seo/triggers` has one — a publish target is per site and
                  `/settings` is per organisation — and with **no sidebar entry**,
                  because S-D3 approved eight SEO sections and this is not one of
                  them. It is reached from the Connections settings panel, which
                  is where a tenant looks for what AugRank is connected to and
                  where `configured_target`'s own refusal sends them, and from
                  the approval queue, which is the moment the question arises. */}
              <Route path="/seo/publish-targets" element={<PublishTargets />} />
              {/* S-B32c. `seo#trail`. Its own route rather than a panel on the
                  dashboard, because it is per *run* where every dashboard panel
                  is per window — the date range that governs the panels above
                  has no meaning for a chain, and a viewer that re-fetched every
                  time somebody changed the period would be answering a question
                  nobody asked. */}
              <Route path="/seo/trail" element={<ReasoningTrail />} />
              {/* S-B31a. The module landing page is now the dashboard rather
                  than `ModuleOverview`'s placeholder: `S-D4`'s funnel is what
                  belongs at `/outreach`, and the empty state it shows when a
                  tenant has nothing is the same sentence the placeholder was
                  standing in for. */}
              <Route path="/outreach" element={<DashboardPage />} />
              {/* S-B19. `outreach#inbox`, `#inbox-one` and `#inbox-zero` are one
                  route, because they are one state machine: which of the three a
                  tenant sees is decided by how many channels are connected, and
                  routing between them would move the URL under somebody who just
                  connected their first one. */}
              <Route path="/outreach/inbox" element={<InboxPage />} />
              {/* S-G8. `outreach#supp` — audit row §A4, over the three
                  `suppression:manage` routes S-B12 shipped and nothing called.
                  Its own route rather than a settings panel: S-D3 approved
                  Suppression as one of Outreach's sections, and the sidebar has
                  rendered it disabled since S-B12 waiting for exactly this. */}
              <Route path="/outreach/suppression" element={<Suppression />} />
              {/* S-B20. `outreach#leads`, `#lead`, `#crm` and `#compose` — audit
                  rows §B1 to §B4. Four screens over one session's API because
                  they are one loop: a lead is found, written to, replies, and
                  moves across the board, and a tenant reads them in that order. */}
              <Route path="/outreach/leads" element={<Leads />} />
              <Route path="/outreach/leads/:leadId" element={<LeadDetail />} />
              <Route path="/outreach/pipeline" element={<Pipeline />} />
              <Route path="/outreach/compose" element={<Compose />} />
              {/* S-B21. `outreach#packs` — audit row §B5, the last open row in
                  `docs/MOCKUP-GAP-AUDIT.md`. One route rather than a keyword
                  screen and a separate packs screen: a pack *is* a way of adding
                  keywords, and the prototype draws the two side by side because
                  the decision a tenant makes is a single one — what do we search
                  for. The post filter is on it for the same reason, and not in
                  `/settings`: *only posts newer than* and *skip posts containing*
                  are read together with the keyword they filter, never apart. */}
              <Route path="/outreach/packs" element={<NichePacks />} />
              {/* S-G9. `outreach#post` — audit row §A5, over S-B17's `POST` and
                  the `GET` this session added beside it. **No sidebar entry**:
                  S-D3 approved seven Outreach sections and this is not one, so
                  it is a `SUBSCREENS` label reached from the LinkedIn card in
                  Settings, exactly as `/seo/publish-targets` is. */}
              <Route path="/outreach/post" element={<PublishPost />} />
              {/* S-B6. Billing is its own surface rather than a ninth settings
                  panel: S-D3 approved eight and `admin.html` draws billing as
                  standalone screens. Reached from the account menu, which is
                  where every SaaS puts it and which already exists. */}
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/billing/plans" element={<PlansPage />} />
              {/* S-B7. `admin#usage` and `admin#overage` are billing screens in
                  the approved design, and the overage prompt is what the usage
                  screen becomes at the ceiling — so one route, not two. */}
              <Route path="/billing/usage" element={<UsagePage />} />
              <Route path="/billing/return" element={<CheckoutReturn />} />
              <Route path="/settings" element={<SettingsLayout />}>
                <Route index element={<Navigate to="/settings/organisation" replace />} />
                <Route path="organisation" element={<OrganisationPanel />} />
                <Route path="team" element={<TeamPanel />} />
                <Route path="brand-voice" element={<BrandVoicePanel />} />
                <Route path="budget" element={<BudgetPanel />} />
                <Route path="connections" element={<ConnectionsPanel />} />
                {/* S-B17. The extension panel is a card inside Connections now
                    (`panels.ts` said it would be). The route stays as a redirect
                    because notification emails sent before this release carry
                    `/settings/extension` in their button — see
                    `app/linkedin/pause.RESUME_PATH`. */}
                <Route path="extension" element={<Navigate to="/settings/connections" replace />} />
                <Route path="notifications" element={<NotificationsPanel />} />
                <Route path="sessions" element={<SessionsPanel />} />
                <Route path="danger-zone" element={<DangerZonePanel />} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}
